// Khung chung cho các màn quản lý POS: Layout CMS + tiêu đề + thanh thao tác + theme màu thương hiệu
import React from 'react';
import { ConfigProvider, Select, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ShoppingCart, FlaskConical } from 'lucide-react';
import Layout from '../../../Layout';
import usePosStore from '../store';
import { BRAND, usePosAuth } from '../utils';
import type { PosRole } from '../data';

export const posTheme = {
  token: { colorPrimary: BRAND.button, colorLink: BRAND.button, borderRadius: 8, fontFamily: 'inherit' },
};

/** Chọn vai trò giả lập — chỉ hiện ở môi trường dev để thử luồng thu ngân / quản lý */
export function DemoRoleSwitch() {
  const demoRole = usePosStore(s => s.demoRole);
  const setDemoRole = usePosStore(s => s.setDemoRole);
  const { realRole } = usePosAuth();
  if (process.env.NODE_ENV !== 'development') return null;
  return (
    <div className="flex items-center gap-1.5 rounded-lg border border-dashed border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-700">
      <Tooltip title="Chỉ có ở môi trường dev: giả lập vai trò để thử hạn mức chiết khấu, duyệt hoàn tiền...">
        <FlaskConical className="h-3.5 w-3.5" />
      </Tooltip>
      <Select<PosRole | 'real'>
        size="small"
        variant="borderless"
        value={demoRole || 'real'}
        onChange={v => setDemoRole(v === 'real' ? null : v)}
        popupMatchSelectWidth={false}
        options={[
          { value: 'real', label: `Vai trò thật (${ROLE_LABEL[realRole]})` },
          { value: 'cashier', label: 'Giả lập: Thu ngân' },
          { value: 'manager', label: 'Giả lập: Quản lý' },
          { value: 'admin', label: 'Giả lập: Admin' },
        ]}
      />
    </div>
  );
}

export const ROLE_LABEL: Record<PosRole, string> = { cashier: 'Thu ngân', manager: 'Quản lý', admin: 'Admin' };

interface Props {
  title: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}

export default function PosShell({ title, subtitle, actions, children }: Props) {
  const navigate = useNavigate();
  const { can } = usePosAuth();
  return (
    <ConfigProvider theme={posTheme}>
      <Helmet><title>{`${title} | Khothi`}</title></Helmet>
      <Layout>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[18px] font-bold text-000000e0">{title}</h1>
            {subtitle && <div className="mt-0.5 text-sm text-slate-500">{subtitle}</div>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <DemoRoleSwitch />
            {actions}
            {can('create') && (
              <button
                onClick={() => navigate('/pos')}
                className="flex h-9 items-center gap-1.5 rounded-lg bg-[#357f96] px-3 text-sm font-semibold text-white hover:bg-[#2c6b7f]"
              >
                <ShoppingCart className="h-4 w-4" /> Bán hàng
              </button>
            )}
          </div>
        </div>
        {children}
      </Layout>
    </ConfigProvider>
  );
}
